// frontend/src/api/client.js

export const API_BASE_URL = (() => {
  const v = (
    process.env.REACT_APP_API_BASE_URL ||
    process.env.VITE_API_BASE_URL ||
    ""
  ).trim();
  if (v) return v.replace(/\/+$/, "");
  if (typeof window !== "undefined") {
    const host = String(window.location?.hostname || "").toLowerCase();
    if (
      host === "estimation-station.com" ||
      host === "www.estimation-station.com"
    ) {
      return "https://estimation-station-api.fly.dev";
    }
  }
  return "http://127.0.0.1:8000";
})();

export const TOKEN_STORAGE_KEY = "estimation_station_token";

function safeJsonParse(text) {
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch (e) {
    return { ok: false, error: e, raw: text };
  }
}

export function getStoredToken() {
  const current = localStorage.getItem(TOKEN_STORAGE_KEY) || "";
  if (current) return current;
  const legacy =
    localStorage.getItem("authToken") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    "";
  if (legacy) {
    localStorage.setItem(TOKEN_STORAGE_KEY, legacy);
  }
  return legacy;
}

function decodeJwtPayload(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return {};
    const payload = parts[1];
    const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    const json = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_e) {
    return {};
  }
}

export function getStoredTokenPayload() {
  return decodeJwtPayload(getStoredToken());
}

export function getStoredUserEmail() {
  const payload = getStoredTokenPayload();
  return String(payload?.email || "").trim().toLowerCase();
}

export function isStoredDevAccount() {
  const expected = String(
    process.env.REACT_APP_DEV_LOGIN_EMAIL ||
      process.env.VITE_DEV_LOGIN_EMAIL ||
      "dev@estimationstation.local"
  )
    .trim()
    .toLowerCase();
  const email = getStoredUserEmail();
  return Boolean(expected && email && email === expected);
}

export function setStoredToken(token) {
  const value = String(token || "").trim();
  if (!value) {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    return;
  }
  localStorage.setItem(TOKEN_STORAGE_KEY, value);
}

export function clearStoredToken() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

export function buildAuthHeaders(extraHeaders = {}) {
  const token = getStoredToken();
  const nextHeaders = { ...extraHeaders };
  if (token && !nextHeaders.Authorization) {
    nextHeaders.Authorization = `Bearer ${token}`;
  }
  return nextHeaders;
}

function snippet(text, max = 200) {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  return s.length > max ? `${s.slice(0, max)}...` : s;
}

function toAbsoluteUrl(pathOrUrl) {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  if (!API_BASE_URL) return pathOrUrl;

  const p = String(pathOrUrl || "");
  return `${API_BASE_URL}${p.startsWith("/") ? p : `/${p}`}`;
}

/**
 * Core fetch wrapper with:
 * - auth header injection
 * - JSON auto-encoding (unless FormData or string)
 * - response text buffering + safe JSON parse
 * - rich error messages (status + detail/snippet)
 */
export async function apiFetch(
  pathOrUrl,
  { method = "GET", headers = {}, body } = {}
) {
  const nextHeaders = buildAuthHeaders(headers);
  let nextBody = body;

  if (body && !(body instanceof FormData) && typeof body !== "string") {
    nextHeaders["Content-Type"] =
      nextHeaders["Content-Type"] || "application/json";
    nextBody = JSON.stringify(body);
  }

  const res = await fetch(toAbsoluteUrl(pathOrUrl), {
    method,
    headers: nextHeaders,
    body: nextBody,
  });

  const text = await res.text();
  const parsed = safeJsonParse(text);

  if (!res.ok) {
    const detail = parsed.ok
      ? parsed.value?.detail ||
        parsed.value?.message ||
        snippet(JSON.stringify(parsed.value))
      : snippet(text);

    const err = new Error(`HTTP ${res.status}: ${detail || "Request failed"}`);
    err.status = res.status;
    err.body = parsed.ok ? parsed.value : text;
    throw err;
  }

  return {
    status: res.status,
    ok: res.ok,
    data: parsed.ok ? parsed.value : text,
    text,
    headers: res.headers,
  };
}

export async function httpJson(pathOrUrl, options = {}) {
  const { data, status } = await apiFetch(pathOrUrl, options);
  if (typeof data === "string") {
    const err = new Error(`JSON parse error (HTTP ${status}): ${snippet(data)}`);
    err.status = status;
    err.body = data;
    throw err;
  }
  return data;
}

export async function httpText(pathOrUrl, options = {}) {
  const { text } = await apiFetch(pathOrUrl, options);
  return text;
}

/**
 * Binary fetch helper (for ZIP/PDF/etc). Uses same auth behavior as apiFetch.
 * Returns a Blob.
 */
export async function httpBlob(pathOrUrl, { method = "GET", headers = {}, body } = {}) {
  const nextHeaders = buildAuthHeaders(headers);

  const res = await fetch(toAbsoluteUrl(pathOrUrl), {
    method,
    headers: nextHeaders,
    body,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const parsed = safeJsonParse(text);
    const detail = parsed.ok
      ? parsed.value?.detail || parsed.value?.message || snippet(JSON.stringify(parsed.value))
      : snippet(text);

    const err = new Error(`HTTP ${res.status}: ${detail || "Request failed"}`);
    err.status = res.status;
    err.body = parsed.ok ? parsed.value : text;
    throw err;
  }

  return await res.blob();
}

/**
 * Binary fetch helper that also returns response headers.
 */
export async function httpBlobWithMeta(pathOrUrl, { method = "GET", headers = {}, body } = {}) {
  const nextHeaders = buildAuthHeaders(headers);

  const res = await fetch(toAbsoluteUrl(pathOrUrl), {
    method,
    headers: nextHeaders,
    body,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const parsed = safeJsonParse(text);
    const detail = parsed.ok
      ? parsed.value?.detail || parsed.value?.message || snippet(JSON.stringify(parsed.value))
      : snippet(text);

    const err = new Error(`HTTP ${res.status}: ${detail || "Request failed"}`);
    err.status = res.status;
    err.body = parsed.ok ? parsed.value : text;
    throw err;
  }

  return {
    blob: await res.blob(),
    headers: res.headers,
    status: res.status,
  };
}

// -------------------- OpenAPI cache + endpoint resolver --------------------

let openApiCache = null;
let openApiPromise = null;

export async function getOpenApi() {
  if (openApiCache) return openApiCache;
  if (openApiPromise) return openApiPromise;

  openApiPromise = (async () => {
    try {
      const data = await httpJson("/openapi.json");
      openApiCache = data;
      return data;
    } finally {
      openApiPromise = null;
    }
  })();

  return openApiPromise;
}

/**
 * Returns the first preferred path that exists in OpenAPI, else null.
 */
export async function resolveEndpoint(pathsPreferred = []) {
  const schema = await getOpenApi();
  const paths =
    schema && typeof schema === "object" && schema.paths && typeof schema.paths === "object"
      ? schema.paths
      : {};

  for (const p of pathsPreferred) {
    if (p && Object.prototype.hasOwnProperty.call(paths, p)) return p;
  }
  return null;
}

export { safeJsonParse };
